import { MetricKey, ReportResponse } from "../api/types";

// ============================================================================
// Tender-type breakdown for the Financial group-by report
// (RP-2153/2154/2155/2157/2159 — Clerk / Location / Vendor / Event / VendorType).
//
// Contract (landed): docs/Reports-API-Contract-Sprint2-QA.md §1 in
// Distributed-Systems — FinancialByDimension advertises 14 additive metrics,
// a $ amount and a transaction count per tender class, on every row and in
// totals. They arrive as ordinary MetricKeys, so the grid renders them like
// any other column; this module only handles DISPLAY ORDER, which must match
// the old reports: the six gross-$ tender columns as one block, then the six
// "# of Transactions" columns as one block, both in the old order
// (Cash, Credit, RFID, Cash Balance, Promo Balance, Tokens).
//
// "Other" (defunct/unknown tenders — Tappit, QR Code) has no old-report
// counterpart; its pair is appended last and only when it carries anything,
// so a QA diff against the old reports sees exactly the old columns.
// ============================================================================

// Old reports' column order within each block.
const TENDER_AMOUNT_ORDER: MetricKey[] = [
  "TenderCashAmount",
  "TenderCreditAmount",
  "TenderRfidAmount",
  "TenderCashBalanceAmount",
  "TenderPromoBalanceAmount",
  "TenderTokenAmount",
];
const TENDER_COUNT_ORDER: MetricKey[] = [
  "TenderCashCount",
  "TenderCreditCount",
  "TenderRfidCount",
  "TenderCashBalanceCount",
  "TenderPromoBalanceCount",
  "TenderTokenCount",
];

const ALL_TENDER_KEYS: ReadonlySet<MetricKey> = new Set<MetricKey>([
  ...TENDER_AMOUNT_ORDER,
  ...TENDER_COUNT_ORDER,
  "TenderOtherAmount",
  "TenderOtherCount",
]);

// Untendered (RP-2155/2159) is the Gross remainder no approved tender covered — the reconciling
// tail of the gross-$ block, NOT a tender class. Deliberately OUTSIDE ALL_TENDER_KEYS/TENDER_ROWS:
// that set also drives the Vertical Sales Payment Breakdown, which must not grow an "Untendered"
// payment row. Only the grid's column ORDER knows about it (see orderMetricsForFinancialGrid).
const UNTENDERED_AMOUNT: MetricKey = "TenderUntenderedAmount";

export const isTenderMetric = (m: MetricKey): boolean => ALL_TENDER_KEYS.has(m);

export const hasTenderMetrics = (data: ReportResponse): boolean =>
  data.metrics.some(isTenderMetric);

// Gate for the Untendered legend (RP-2155/2159). True only when the engine actually ADVERTISED the
// column — against today's prod API it is false everywhere, so the footnote never claims a column
// that isn't on screen.
export const hasUntenderedMetric = (data: ReportResponse): boolean =>
  data.metrics.includes(UNTENDERED_AMOUNT);

// ---------------------------------------------------------------------------
// Vertical Sales "Payment Breakdown" — derived from the FINANCIAL response's
// CAPPED Tender* totals (the "Gross <Tender> Sales" figures on the same basis
// as the Gross line), NOT from PaymentMix.
//
// Why (QA 2026-07-13): PaymentMix amounts are money COLLECTED — approved
// transaction amounts INCLUDING tips + the service fee. That is correct for a
// payment view, but the old Vertical Sales PDF's "Payment Breakdown" showed
// the capped legacy gross_* buckets, and this section renders directly under
// the Gross/Net summary — so it must use the capped figures or QA sees every
// row inflated by the tip/fee share.
//
// It totals to Σ tender $, which equals Gross only when Untendered is zero
// (RP-2155/2159): Untendered is NOT a tender class and must not become a
// payment row. The page and the PDF therefore carry
// UNTENDERED_TENDER_ONLY_NOTE so the shortfall reads as a definition, not a
// missing row.
// ---------------------------------------------------------------------------
const TENDER_ROWS: { amount: MetricKey; count: MetricKey; label: string }[] = [
  { amount: "TenderCashAmount", count: "TenderCashCount", label: "Cash" },
  { amount: "TenderCreditAmount", count: "TenderCreditCount", label: "Credit" },
  { amount: "TenderRfidAmount", count: "TenderRfidCount", label: "RFID" },
  { amount: "TenderCashBalanceAmount", count: "TenderCashBalanceCount", label: "Cash Balance" },
  { amount: "TenderPromoBalanceAmount", count: "TenderPromoBalanceCount", label: "Promo Balance" },
  { amount: "TenderTokenAmount", count: "TenderTokenCount", label: "Tokens" },
  { amount: "TenderOtherAmount", count: "TenderOtherCount", label: "Other" },
];

// One row per tender class that carries value, shaped as a ReportResponse so
// MetricGrid / the PDF's MetricTable render it like any report. Columns reuse
// the Gross ("Gross Sales") and TransactionCount ("Transactions") metric keys —
// the amounts ARE the capped gross sales portion, so the label is truthful.
export function paymentBreakdownFromFinancial(financial: ReportResponse): ReportResponse | null {
  if (!hasTenderMetrics(financial)) return null;
  const t = financial.totals;
  const rows = TENDER_ROWS.filter(
    (c) => (t[c.amount] ?? 0) !== 0 || (t[c.count] ?? 0) !== 0,
  ).map((c) => ({
    key: c.label,
    label: c.label,
    group: null,
    values: { Gross: t[c.amount] ?? 0, TransactionCount: t[c.count] ?? 0 },
  }));
  return {
    ...financial,
    metrics: ["Gross", "TransactionCount"],
    rows,
    totals: {
      Gross: rows.reduce((acc, r) => acc + (r.values.Gross ?? 0), 0),
      TransactionCount: rows.reduce((acc, r) => acc + (r.values.TransactionCount ?? 0), 0),
    },
  };
}

// ---------------------------------------------------------------------------
// Legacy column order (RP-2155/2159) — mirrored column-for-column from the OLD
// Vendor Sales / Vendor Type Sales report:
//   src/modules/reports/point-of-sale/vendor-sales-report/helpers.tsx
// (the same colDefs back the Vendor Type variant). 28 metric columns follow the
// "Vendor Name" key column and map to the 27 keys below; the legacy header is on
// each line.
//
// 27, not 28, because the legacy "Net Deduction - Token Sales" column reads the
// SAME gross_token_sales field as "Gross Token Sales" (helpers.tsx:173 vs :96).
// One MetricKey can only be one grid column, so Token sales appears once, in its
// gross-$ position — the duplicate is dropped, not lost.
//
// Keys the engine has not advertised yet are filtered out at the end, so this
// list is safe to ship ahead of the engine deploy — an unshipped metric simply
// no-ops instead of rendering an empty column.
// ---------------------------------------------------------------------------
const LEGACY_COLUMN_ORDER: MetricKey[] = [
  "Gross", //                    Total Gross Sales
  ...TENDER_AMOUNT_ORDER, //     Gross Cash/Credit/RFID/Cash Balance/Promo Balance/Token Sales
  "NetSales", //                 Total Net Sales
  "DiscountExPromo", //          Net Deduction - Discounts
  "PromoDiscount", //            Net Deduction - Promo Codes
  "RefundsPreTax", //            Net Deduction - Refunds  (legacy net_refunds)
  "Tax", //                      Net Deduction - Taxes
  "NetPromoBalanceSales", //     Net Deduction - Promo Balance Sales
  "Tip", //                      Total Tips
  "TenderCreditTip", //          Credit Tips
  "TenderRfidTip", //            RFID Tips
  "TenderCashBalanceTip", //     Cash Balance Tips
  "TenderPromoBalanceTip", //    Promo Balance Tips
  "Surcharge", //                Total Service Fee
  "Refunds", //                  Total Refunds Value
  "MerchantProcessed", //        Total Merchant Processed
  ...TENDER_COUNT_ORDER, //      # of Transactions - Cash…Tokens
];

// Grid column order for a FinancialByDimension response: the legacy Vendor Sales
// layout above, then whatever else the engine advertised (Orders, total
// Transactions, the combined Discount, the remaining per-tender tips), in engine
// order — the legacy grid has no such column, so they trail rather than shift one.
//
// Two columns have no legacy counterpart but DO belong inside a legacy block, so
// they are spliced in rather than left to trail:
//   • Untendered CLOSES the gross-$ block (Σ tender $ + Untendered == Gross) —
//     the engine advertises it right after Refunds, which would split the
//     canonical money run.
//   • the Other pair (defunct/unknown tenders) tails its own block, and only
//     when it carries a nonzero total, so a QA diff sees exactly the old columns.
export function orderMetricsForFinancialGrid(data: ReportResponse): MetricKey[] {
  const advertised = new Set(data.metrics);
  if (!hasTenderMetrics(data)) return [...data.metrics];

  const includeOther =
    (data.totals.TenderOtherAmount ?? 0) !== 0 || (data.totals.TenderOtherCount ?? 0) !== 0;

  const legacy: MetricKey[] = [];
  for (const m of LEGACY_COLUMN_ORDER) {
    legacy.push(m);
    if (m === "TenderTokenAmount") {
      if (includeOther) legacy.push("TenderOtherAmount");
      legacy.push(UNTENDERED_AMOUNT);
    }
    if (m === "TenderTokenCount" && includeOther) legacy.push("TenderOtherCount");
  }

  // Richness the legacy reports never had (Orders, total Transactions, combined Discounts, the
  // remaining per-tender tips) trails the legacy block instead of shifting a legacy column. The
  // Other pair is ALWAYS considered placed — its position is decided by includeOther above, so a
  // suppressed (all-zero) Other must not reappear here as an extra.
  const placed = new Set<MetricKey>([...legacy, "TenderOtherAmount", "TenderOtherCount"]);
  const extras = data.metrics.filter((m) => !placed.has(m));

  return [...legacy.filter((m) => advertised.has(m)), ...extras];
}
